// Flow step type definitions and helpers

export const StepKinds = {
  input: "input",       // user enters a value; label + placeholder
  list: "list",         // shows a list based on previous context; select one
  dateRange: "dateRange", // from/to dates
  lookup: "lookup",     // executes a mid-flow search and shows results as selectable table
};

/**
 * Step role in search construction:
 *  "database" – the selected value names the database to search
 *  "field"    – the selected value is the field/index name to search in
 *  "value"    – the entered value is what to search for (paired with "field" role)
 * Leave undefined / empty for steps that only affect list loading context.
 */
export const StepRoles = {
  database: "database",
  field: "field",
  value: "value",
};

export const defaultStep = (kind = StepKinds.input) => {
  switch (kind) {
    case StepKinds.input:
      return {
        kind,
        label: "Customer Number",
        placeholder: "Enter customer no",
        key: "customerNumber",
        searchField: "",  // set to a field name for direct index search
        role: "",         // or use StepRoles for dynamic search
      };
    case StepKinds.list:
      return {
        kind,
        label: "Choose Group",
        source: "groups",
        key: "group",
        role: "",
        dataMode: "static", // static | api
        staticItems: ["Credit Cards", "Loans", "Accounts"],
        api: {
          url: "",        // e.g. /api/Flow/datasources/databases
          method: "GET",
          headers: {},
          valuePath: null,
        },
      };
    case StepKinds.dateRange:
      return {
        kind,
        label: "Statement Dates",
        fromKey: "fromDate",
        toKey: "toDate",
        searchField: "",  // set to a field name to include in search
      };
    case StepKinds.lookup:
      return {
        kind,
        label: "Lookup Results",
        key: "selectedItem",           // context key for selected row value
        selectField: "",               // which result field becomes the selected value
        columns: [],                   // which result fields to show in table
        contextMappings: [],           // [{ from: "ResultField", to: "contextKey" }]
        searchConfig: {                // search to execute (reuses flow search model)
          databases: [],
          fieldMappings: [],           // [{ contextKey, fieldName, type: "value"|"dateFrom"|"dateTo" }]
        },
      };
    default:
      return { kind };
  }
};

// ─── Search Config Helpers ────────────────────────────────────────────────────

/**
 * Build a FlowSearchConfig from the flow definition and accumulated context.
 * This is what gets sent to POST /api/Flow/search.
 *
 * The flow's searchConfig (if present) drives the construction.
 * As a fallback, step roles (StepRoles) are used.
 *
 * @param {object} flow  - the full flow definition
 * @param {object} ctx   - accumulated user context { key: value }
 * @returns {{ context: object, searchConfig: object }}
 */
export function buildFlowSearchPayload(flow, ctx) {
  const explicitConfig = flow.searchConfig || null;

  if (explicitConfig) {
    // Use the designer-supplied config directly (may contain templates resolved server-side)
    return { context: ctx, searchConfig: explicitConfig };
  }

  // ── Derive config from step roles ────────────────────────────────────────
  const steps = flow.steps || [];
  const config = {};

  // Look for a "database" role step
  const dbStep = steps.find((s) => s.role === StepRoles.database);
  if (dbStep) {
    config.databaseContextKey = dbStep.key;
  }

  // Look for "field" + "value" role steps
  const fieldStep = steps.find((s) => s.role === StepRoles.field);
  const valueStep = steps.find((s) => s.role === StepRoles.value);
  if (fieldStep && valueStep) {
    config.fieldContextKey = fieldStep.key;
    config.valueContextKey = valueStep.key;
  }

  // Collect direct searchField mappings from input steps
  const fieldMappings = [];
  for (const st of steps) {
    if (st.kind === StepKinds.input && st.searchField && ctx[st.key]) {
      fieldMappings.push({
        contextKey: st.key,
        fieldName: st.searchField,
        type: "value",
      });
    }
    if (st.kind === StepKinds.dateRange && st.searchField) {
      if (ctx[st.fromKey]) {
        fieldMappings.push({
          contextKey: st.fromKey,
          fieldName: st.searchField,
          type: "dateFrom",
        });
      }
      if (ctx[st.toKey]) {
        fieldMappings.push({
          contextKey: st.toKey,
          fieldName: st.searchField,
          type: "dateTo",
        });
      }
    }
  }

  if (fieldMappings.length > 0) config.fieldMappings = fieldMappings;

  return { context: ctx, searchConfig: config };
}

/**
 * Build search payload for a lookup step's inline search.
 * Uses the step's own searchConfig + accumulated context.
 */
export function buildLookupSearchPayload(step, ctx) {
  const cfg = step.searchConfig || {};
  return { context: ctx, searchConfig: cfg };
}

// ─── Flow Store ───────────────────────────────────────────────────────────────

/**
 * FlowStore: localStorage-backed cache with optional server sync.
 * The server is the source of truth; localStorage is a fallback.
 */
export const FlowStore = {
  /** Load all flows from localStorage cache. */
  loadAll() {
    try {
      return JSON.parse(localStorage.getItem("flows") || "[]");
    } catch {
      return [];
    }
  },

  /** Save all flows to localStorage. */
  saveAll(flows) {
    try {
      localStorage.setItem("flows", JSON.stringify(flows));
    } catch {}
  },

  /** Insert or update a flow in localStorage by name. */
  upsert(flow) {
    const all = FlowStore.loadAll();
    const idx = all.findIndex((f) => f.name === flow.name);
    if (idx >= 0) all[idx] = flow;
    else all.push(flow);
    FlowStore.saveAll(all);
  },

  /** Remove a flow from localStorage by name. */
  remove(name) {
    const all = FlowStore.loadAll().filter((f) => f.name !== name);
    FlowStore.saveAll(all);
  },

  /** Replace the entire cache with server data. */
  syncFromServer(serverFlows) {
    if (Array.isArray(serverFlows)) FlowStore.saveAll(serverFlows);
  },
};
